import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {useGlobalNotificationMessage} from "../context/message";
import {useStripePayments} from "../context/stripe";
import {usePaymentService} from "../services/payment-service";
import {Modal} from "react-bootstrap";
import Section from "./section";
import HelperService from "../services/helper-service";
import {useEventService} from "../services/event-service";
import Loading from "./loading";
import {getSessionData, setSessionData} from "../modules/session-persistor";
import {AppEvents, AppSessionKeys} from "../utils/constants";
import {useNotifyEventsService} from "../services/notify-events-service";
import StripePaymentMethods from "./stripe-payment-methods";


const StripeEventTipModal = forwardRef( ({ ...props}, ref) => {
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(false);
    const [tipValue, setTipValue] = useState('');
    const [tipVerify, setTipVerify] = useState(null);
    const [eventId, setEventId] = useState(null);
    const [tipData, setTipData] = useState({});
    const [showCardManagementModal, setShowCardManagementModal] = useState(false);
    const [event, setEvent] = useState(null);
    const {addErrorMessage, addSuccessMessage} = useGlobalNotificationMessage();
    const {initStripe} = useStripePayments();

    const ps = usePaymentService();
    const es = useEventService();
    const nes = useNotifyEventsService();

    //expose api for interacting with component
    useImperativeHandle(ref, () => ({
        openModal: openModal,
        closeModal: closeModal,
    }));

    useEffect(() => {
        nes.on(AppEvents.paymentMethodsClosed, resolveContinueTiping);
        nes.on(AppEvents.showPaymentMethods, openStripeCardManagement);
        return () => {
            nes.off(AppEvents.paymentMethodsClosed, resolveContinueTiping);
            nes.off(AppEvents.showPaymentMethods, openStripeCardManagement);
        }
    }, []);

    const openModal = ({eventId, event=null}) => {
        setEventId(eventId);
        setLoading(true);
        setTipValue('');

        if (!event || event?.id!==eventId){
            setEvent(null);
            //get event detail
            es.getDetail(eventId).then((detail) => {
                setEvent(detail?.events?.length>0 ? detail?.events[0] : null);
                //show ui with amount
                setLoading(false);
            })
        } else {
            setEvent(event);
            setLoading(false);
        }

        setShow(true);
    }

    const closeModal = () => setShow(false);

    const createAndConfirmEventTipIntent = (eid=null, tip=null) => {
        setSubmitting(true);

        ps.verifyTipPaymentIntent({eventId: eid ? eid : eventId, price: tip ? tip : tipValue})
            .then((verifyTip) => {

                if (verifyTip && verifyTip.text && verifyTip.code) {
                    if ('OK'===verifyTip.code){
                        //create payment intent upon confirmation
                        setTipVerify(verifyTip);
                        setSubmitting(false);
                        setShow(false);
                    } else {
                        addErrorMessage(verifyTip.text);
                        setSubmitting(false);
                    }
                }

            })
            .catch(error => {
                HelperService.handleApiErrors(error, addErrorMessage);
                setSubmitting(false);
            })
    }

    const changeHandler = event => {
        setTipValue(event.target.value);
    };

    const blurHandler = event => {
        let val = parseFloat(event.target.value);
        if (!isNaN(val)) {
            setTipValue(val.toFixed(2));
        }
    };

    const createAndConfirmPaymentIntent = () => {
        setSubmitting(true);
        //create paymentIntent
        ps.createTipPaymentIntent({eventId: eventId, price: tipValue})
            .then((res) => {

                if (!res.stripeAccount || !res.paymentIntent?.id || !res.paymentIntent.client_secret) {
                    throw new Error('Payment failed. Intent did not have all necessary information.');
                }

                //init stripe
                initStripe(res?.paymentIntent?.client_secret, res.stripeAccount)
                    .then(async (newStripe)=>{
                        //confirm paymentIntent
                        const result = await newStripe.confirmCardPayment(res?.paymentIntent?.client_secret);
                        if (result?.error) {
                            // Show error to your customer (e.g., insufficient funds)
                            throw new Error(result.error.message);
                        } else {
                            if (result.paymentIntent.status === 'requires_capture') {
                                es.tip(eventId, res.paymentIntent.id)
                                    .then(async (tip)=>{
                                        closeModal();
                                        setTipVerify(null);
                                        addSuccessMessage('Thank you for your tip!');
                                        setSubmitting(false);
                                    })
                                    .catch((error) => {
                                        HelperService.handleApiErrors(error, addErrorMessage);
                                        setSubmitting(false);
                                    })
                            } else {
                                addErrorMessage('We tried to alocate funds but stripe responded with unexpected status, we cannot continue: '+result?.paymentIntent?.status);
                            }
                        }

                    })
                    .catch((error) => {
                        HelperService.handleApiErrors(error, addErrorMessage);
                        setSubmitting(false);
                    })

            })
            .catch(error => {
                HelperService.handleApiErrors(error, addErrorMessage);
                setSubmitting(false);
            })
    }

    const showPaymentMethods = ()=>{
        let _tipData = {eventId: eventId, tip: tipValue};
        setTipData(_tipData);
        setSessionData(AppSessionKeys.continueWithTiping, _tipData);
        nes.trigger(AppEvents.showPaymentMethods, eventId);
    };

    const resolveContinueTiping = (e=null)=>{
        let continueTiping = getSessionData(AppSessionKeys.continueWithTiping);
        if (!continueTiping || !continueTiping?.eventId || !continueTiping?.tip){ return false; }
        createAndConfirmEventTipIntent(continueTiping.eventId, continueTiping.tip);
    }

    const openStripeCardManagement = (e=null) => {
        setTipVerify(null);
        setShowCardManagementModal(true);
    }

    return (
        <>
            {show && <Modal size='md' show={!!show} onHide={closeModal} centered>
                <Modal.Header closeButton>
                    <h4 className="header-stripe-blue pb-1">Tip trainer</h4>
                </Modal.Header>
                <Modal.Body className="p-5">
                    <Section animation={HelperService.getRandomAnimation()}>
                        <div className="row pb-3">
                            <div className="col-sm-12 mb-3">
                                {loading && <div className="text-center py-3"><Loading /></div>}

                                {!loading && event && <>

                                    {!event?.price?.tipsEnabled && <>
                                        <p>Trainer is not accepting tips at the moment. Thank you for understanding.</p>
                                    </>}

                                    {event?.price?.tipsEnabled && <>
                                        <div className="form-group mb-3">
                                            <p>Please choose amount to tip and confirm.</p>
                                            <div className="input-group mb-3">
                                                <div className="input-group-prepend">
                                                    <span className="input-group-text">$</span>
                                                </div>
                                                <input required="required" type="text" placeholder='5.00' className="form-control" value={tipValue} onChange={changeHandler} onBlur={blurHandler} />
                                            </div>

                                        </div>
                                        {!submitting && <button className="btn btn-primary" disabled={!tipValue || isNaN(tipValue) || tipValue<1} onClick={() => createAndConfirmEventTipIntent()}>Tip now</button>}
                                        {submitting && <Loading/>}
                                    </>}
                                </>}
                            </div>
                        </div>
                    </Section>
                </Modal.Body>
            </Modal>}

            {tipVerify!==null && <Modal size='md' show={tipVerify!==null} onHide={()=>setTipVerify(null)} centered>
                <Modal.Header closeButton>
                    <h4 className="header-stripe-blue pb-1">Payment confirmation</h4>
                </Modal.Header>
                <Modal.Body className="p-5">
                    <Section animation={HelperService.getRandomAnimation()}>
                        <div className="row pb-3">
                            <div className="col-sm-12 mb-3">
                                <p>{tipVerify?.text}</p>
                            </div>
                            <div className="col-sm-12 text-center mb-3">
                                {!submitting && <button className="btn btn-primary" onClick={()=>createAndConfirmPaymentIntent()}>Confirm</button>}
                                {!submitting && <button className="ms-2 btn btn-outline-primary" onClick={()=>showPaymentMethods()}>Manage payment methods</button>}
                                {submitting && <Loading />}
                            </div>
                        </div>
                    </Section>
                </Modal.Body>
            </Modal>}

            {showCardManagementModal && <Modal size='lg' show={!!showCardManagementModal} onHide={() => {
                nes.trigger(AppEvents.paymentMethodsClosed, eventId);
                setShowCardManagementModal(false);
            }} centered>
                <Modal.Header closeButton>
                    <h4 className="header-stripe-blue pb-1">Manage payment methods</h4>
                </Modal.Header>
                <Modal.Body className="p-5">
                    <Section animation={HelperService.getRandomAnimation()}>
                        <div className="row pb-3">
                            <div className="col-sm-12 mb-3">
                                <StripePaymentMethods />
                            </div>
                        </div>
                    </Section>
                </Modal.Body>
            </Modal>}
        </>
    )
});

export default StripeEventTipModal;